import { Button, Divider, Grid, Stack, Typography } from '@mui/material'
import styled from 'styled-components/macro'
import { switchProp } from 'styled-tools'
import { DateTime } from 'luxon'

import PassedIcon from '@mui/icons-material/CheckCircleOutline'
import FailedIcon from '@mui/icons-material/HighlightOff'
import CertificateIcon from '@mui/icons-material/StarsOutlined'
import UserIcon from '@mui/icons-material/AccountCircleOutlined'
import NumOfQuestionsIcon from '@mui/icons-material/HelpOutlineOutlined'
import TimeLimitIcon from '@mui/icons-material/AccessTime'

import { CertificateState, PublicCertificate } from 'src/types/graphql'
import { CertificateQuestion } from 'src/types/CertificateQuestion'
import { getCertificateStateName } from 'src/components/CertificateManagement/CertificateState'
import { CertificateQuestionDetails } from 'src/components/CertificateManagement/CertificateQuestionDetails/CertificateQuestionDetails'
import { palette } from 'src/utils/styled'
import { Icon } from '../Icon'

const DataGridItem = styled(Grid)<{ color: 'primary' | 'secondary' | 'success' | 'error' }>`
    display: flex;
    align-items: center;

    color: ${switchProp('color', {
        primary: palette((p) => p.primary.main),
        secondary: palette((p) => p.secondary.main),
        success: palette((p) => p.success.main),
        error: palette((p) => p.error.main),
    })};
`

type CertificateStatsDisplayProps = {
    certificate: PublicCertificate
    onBackToTraiwiss: () => void
}

export function CertificateStatsDisplay({ certificate, onBackToTraiwiss }: CertificateStatsDisplayProps) {
    const questionsAnsweredCorrectlyCount = certificate.questions.reduce(
        (count, q: CertificateQuestion) => (q.answered && q.points === q.receivedPoints ? count + 1 : count),
        0,
    )

    const timeUsed = DateTime.fromJSDate(certificate.quizFinishedAt!).diff(DateTime.fromJSDate(certificate.quizStartedAt!))

    const isTimeUsedOverOneHour = timeUsed.as('hours') >= 1
    const timeUsedFormat = isTimeUsedOverOneHour ? 'hh:mm' : 'mm:ss'
    const timeUsedSuffix = isTimeUsedOverOneHour ? 'h' : 'min'

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: 1 }}>
                <Typography variant="h4" fontWeight="bold">
                    {getGreeting(certificate.state, certificate.user.firstname)}
                </Typography>
            </Grid>
            <DataGridItem item xs={3} color={certificate.state === CertificateState.Passed ? 'success' : 'error'}>
                <Icon>{getStateIcon(certificate.state)}</Icon>
                <b>{getCertificateStateName(certificate.state)}</b>
            </DataGridItem>
            <DataGridItem item xs={2} color="secondary">
                <Icon tooltip="Zertifikat">
                    <CertificateIcon />
                </Icon>
                {certificate.certificateConfiguration.name}
            </DataGridItem>
            <DataGridItem item xs={6} color="secondary">
                <Icon tooltip="Teilnehmer">
                    <UserIcon />
                </Icon>
                <b>{certificate.user.name}</b>&nbsp;({certificate.user.email})
            </DataGridItem>
            <DataGridItem item xs={3} color="secondary">
                <Icon tooltip="Anzahl richtig beantwortete Fragen / Gesamt">
                    <NumOfQuestionsIcon />
                </Icon>
                {questionsAnsweredCorrectlyCount} / {certificate.numOfQuestions} Fragen ({certificate.receivedPointsPercentage} %)
            </DataGridItem>
            <DataGridItem item xs={2} color="secondary">
                <Icon tooltip="Benötigte Zeit">
                    <TimeLimitIcon />
                </Icon>
                {timeUsed.toFormat(timeUsedFormat)} {timeUsedSuffix}
            </DataGridItem>
            <Grid item xs={12}>
                <Divider />
            </Grid>

            <Grid item xs={12} mt={2} mb={2}>
                <DataGridItem item xs={6} color="secondary">
                    <Typography paddingBottom={2}>Ihr Zertifikat können Sie im Dashboard im Bereich „Mein Zertifikat“ einsehen</Typography>
                </DataGridItem>
                <DataGridItem item xs={4} color="secondary">
                    <Button variant="contained" onClick={() => onBackToTraiwiss()}>
                        Zurück zu Traiwiss
                    </Button>
                </DataGridItem>
            </Grid>
            <Grid item xs={12}>
                <Grid item xs={12}>
                    <Typography variant="h5" mb={2}>
                        Einblick in deine Fragen
                    </Typography>
                    <Stack spacing={2}>
                        {certificate.questions.map((q) => (
                            <CertificateQuestionDetails key={q._id} question={q} />
                        ))}
                    </Stack>
                </Grid>
            </Grid>
        </Grid>
    )
}

function getGreeting(state: CertificateState, name: string) {
    switch (state) {
        case CertificateState.Passed:
            return `Glückwunsch, ${name}!`
        case CertificateState.Failed:
            return `Das hat leider noch nicht ganz gereicht, ${name}.`
    }
}

function getStateIcon(state: CertificateState) {
    switch (state) {
        case CertificateState.Passed:
            return <PassedIcon />
        case CertificateState.Failed:
            return <FailedIcon />
    }
}
